// @ts-nocheck
import type { ReactElement } from "react";

import dayjs from "dayjs";
import { renderToStaticMarkup } from "react-dom/server";

import { combineConfigs, barChartConfig, defaultConfig } from "@similarweb/pro-logic-components";
import { ChangeTooltip } from "@similarweb/ui-components";

import type { TimeGranularityName } from "app/common/utils/timeGranularity";
import type ColorStack from "app/components/colorsStack/ColorStack";
import { toggleItems } from "app/components/widget/widget-utilities/time-granularity";
import { minAbbrNumberFilter, pctSignFilter, percentageSignFilter } from "app/filters/ngFilters";
import type {
    IChart,
    EnrichedData,
    IEnrichedDataMetrics,
    IRawData,
} from "app/pages/industry-analysis/players/market-shares/types";
import { getChartDataWithOthers } from "app/pages/industry-analysis/players/market-shares/utils/parseRawData";
import { getApiKey } from "app/pages/industry-analysis/players/market-shares/utils/utils";
import { OTHERS_DOMAIN_NAME } from "app/pages/keyword-analysis/TotalTrafficPage/Graph/GraphData";
import { getAverage } from "app/UtilitiesAndConstants/UtilityFunctions/statistics";
import { areaChartConfig } from "pro-features/components/Chart/src/configs/areaChart/areaChartConfig";
import { SegmentSubtitle } from "pro-features/pages/segments/components/benchmarkOvertime/SegmentsVsGroupLineChart";

import { getColumnChartData, getDataWithOthers, getIsOtherEmpty, removeOthersData } from "./helpers";
import { TooltipWrapper } from "./styles";

type FormatterType = (val: number) => string;
type ChartFormatterType = (val: number) => ReactElement;

export type FormattersType = FormatterType | ChartFormatterType;

interface IMarketPlayersChartSeries extends IChart {
    domain?: string;
    isSegment?: boolean;
}

export const getBarChartConfig = (
    data: EnrichedData,
    averageValueMetric: keyof IEnrichedDataMetrics,
    chartDisplayType: number,
    formatter: FormattersType,
    forWidget: string | string[],
) => {
    const isPercentage = chartDisplayType === 1;

    const columnChartData = getColumnChartData(data, averageValueMetric);

    const dataWithOthers = isPercentage ? getDataWithOthers(columnChartData) : removeOthersData(columnChartData);

    const valueFormatter: FormattersType = isPercentage ? (val) => pctSignFilter()(val) : formatter;

    const yAxisFormatter = isPercentage
        ? ({ value }) => pctSignFilter()(value)
        : ({ value }) => minAbbrNumberFilter()(value);

    const finalConfig = combineConfigs(
        {
            data: [{ data: dataWithOthers }],
            type: "column",
            // @ts-expect-error - valueFormatter is a function that returns a string
            valueFormatter,
            yAxisFormatter,
        },
        [
            barChartConfig,
            defaultConfig,
            {
                chart: { height: 230 },
                series: [{ data: dataWithOthers }],
                tooltip: {
                    shared: true,
                    outside: true,
                    useHTML: true,
                    followPointer: false,
                    enabled: true,
                    formatter() {
                        const changeTooltipProps = {
                            rowHeight: 34,
                            header: forWidget,
                            tableHeaders: [
                                { position: 1, displayName: "" },
                                { position: 0, displayName: "" },
                            ],
                            tableRows: dataWithOthers.map(({ domain, id, color, isSegment, name, y }) => {
                                return {
                                    displayName: domain ?? id,
                                    subtitle: isSegment ? <SegmentSubtitle>{name}</SegmentSubtitle> : null,
                                    value: valueFormatter(y),
                                    color,
                                };
                            }),
                            showChangeColumn: false,
                        };

                        return renderToStaticMarkup(
                            <TooltipWrapper>
                                <ChangeTooltip {...changeTooltipProps} />
                            </TooltipWrapper>,
                        );
                    },
                },
            },
        ],
    );

    return { finalConfig, dataWithOthers };
};

export const getTableChartConfig = (
    formatter: (highChartRef) => ReactElement,
    chartDisplayType: number,
    marketSharesTimeGranularity?: TimeGranularityName,
) => {
    const isPercentage = chartDisplayType === 1;

    const yAxisFormatter = isPercentage
        ? ({ value }) => percentageSignFilter()(value, 2)
        : ({ value }) => minAbbrNumberFilter()(value);

    const granularityName: TimeGranularityName = marketSharesTimeGranularity ?? "Monthly";
    const timeGranularityMeta = toggleItems[granularityName];

    const args = {
        tooltipFormatter: formatter,
        yAxisFormatter: yAxisFormatter,
        xAxisFormatter: ({ value }) => dayjs.utc(value).format(timeGranularityMeta.format),
        timeGranularity: timeGranularityMeta,
        additionalChartConfig: { yAxis: { ceiling: isPercentage ? 1 : null } },
    };

    const config = areaChartConfig(args);

    return config;
};

export const getAreaChartConfig = (
    parsedGraphData: IRawData,
    colors: ColorStack,
    chartDisplayType: number,
    forWidget: string | string[],
    formatter: (highChartRef) => ReactElement,
    valueFormatter: (val: number) => string,
    marketSharesTimeGranularity?: TimeGranularityName,
) => {
    const isPercentage = chartDisplayType === 1;

    const ApiDisplayType = getApiKey(chartDisplayType);

    const dataByGraphType = parsedGraphData[ApiDisplayType].map((item) => {
        return {
            ...item,
            color: item.id !== "Others" ? colors.acquire() : item.color,
        };
    });

    let chartData = isPercentage
        ? getChartDataWithOthers(dataByGraphType, parsedGraphData[ApiDisplayType])
        : removeOthersData(dataByGraphType);
    const isOtherEmpty = getIsOtherEmpty(chartData as IChart[]);

    if (isOtherEmpty) {
        chartData = removeOthersData(chartData);
    }

    const tooltipOtherData: IMarketPlayersChartSeries[] = (chartData as IMarketPlayersChartSeries[]).map((item) => {
        if (item.id === OTHERS_DOMAIN_NAME) {
            return {
                ...item,
                average: item.average ?? getAverage(item.data),
            };
        }

        return item;
    });

    const areaConfig = getTableChartConfig(formatter, chartDisplayType, marketSharesTimeGranularity);

    const granularityName = marketSharesTimeGranularity ?? "Monthly";
    const categoryDateFormat = toggleItems[granularityName].format;

    const config = combineConfigs(
        {
            type: "area",
            // @ts-expect-error - valueFormatter is a function that returns a string
            valueFormatter,
        },
        [
            areaConfig,
            defaultConfig,
            {
                chart: {
                    height: 220,
                    spacing: [10, 10, 10, 10],
                },
                series: chartData,
                tooltip: {
                    shared: true,
                    outside: true,
                    useHTML: true,
                    followPointer: false,
                    enabled: true,
                    formatter() {
                        const changeTooltipProps = {
                            rowHeight: 34,
                            header: dayjs.utc(this.category).format(categoryDateFormat),
                            tableHeaders: [
                                { position: 1, displayName: "" },
                                { position: 0, displayName: "" },
                            ],
                            tableRows: tooltipOtherData.map(({ domain, id, color, data, isSegment, name }) => {
                                return {
                                    displayName: domain ?? id,
                                    subtitle: isSegment ? <SegmentSubtitle>{name}</SegmentSubtitle> : null,
                                    value: formatter(data[this.index].y),
                                    color,
                                };
                            }),
                            showChangeColumn: false,
                        };

                        return renderToStaticMarkup(
                            <TooltipWrapper>
                                <ChangeTooltip {...changeTooltipProps} />
                            </TooltipWrapper>,
                        );
                    },
                },
            },
        ],
    );

    return { config, chartData };
};
